var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { interpolateLab, range } from 'd3';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { Canvas } from '../components/Canvas';
import { defaultMargins } from '../utils';
import { Legend } from '../components/Legend';
import { BarGroups } from './components/BarGroups';
import { sortAlphabetically } from './utils/sortAlphabetically';
import { Axis } from '../Axis';
import { useCharts } from '../components/Charts';
import { getAxesConfig } from './utils/getAxesConfig';
import { GridRows } from '../Grid';
import { palette } from '../../constants';
export function GroupedBar(_a) {
    var series = _a.series, accessors = _a.accessors, marginProps = _a.margins, height = _a.height, tooltip = _a.tooltip, defaultActive = _a.defaultActive, _b = _a.colorPalette, colorPalette = _b === void 0 ? [palette.orange4, palette.orange8] : _b, crosshatchColor = _a.crosshatchColor, _c = _a.hideLegend, hideLegend = _c === void 0 ? false : _c, _d = _a.hideGrid, hideGrid = _d === void 0 ? false : _d, _e = _a.axesProps, axesProps = _e === void 0 ? {} : _e, legendFormat = _a.legendFormat;
    var width = useCharts().width;
    var margins = defaultMargins(marginProps);
    var boundedWidth = width - margins.left - margins.right;
    var boundedHeight = height - margins.top - margins.bottom;
    // go through each data object of series to get the complete list
    // of keys of the data  object
    var groupKeys = React.useMemo(function () {
        var keySet = new Set();
        Object.values(series).forEach(function (s) {
            Object.keys(accessors.data(s)).forEach(function (k) { return keySet.add(k); });
        });
        return sortAlphabetically(Array.from(keySet.values()));
    }, [accessors, series]);
    var _f = React.useState(new Set(defaultActive !== null && defaultActive !== void 0 ? defaultActive : groupKeys)), activeSet = _f[0], setActiveSet = _f[1];
    var xDomain = React.useMemo(function () { return Object.keys(series); }, [series]);
    var xAxisDomain = React.useMemo(function () { return Object.values(series).map(accessors.name); }, [series]);
    var yDomain = React.useMemo(function () {
        var yDomainMax = Math.max.apply(Math, Object.values(series).map(function (s) {
            var activeSeries = Object.entries(accessors.data(s))
                .filter(function (_a) {
                var key = _a[0];
                return activeSet.has(key);
            })
                .map(function (_a) {
                var _b, _c;
                var _ = _a[0], value = _a[1];
                var yValue = accessors.y(value);
                var y1Value = (_c = (_b = accessors.y1) === null || _b === void 0 ? void 0 : _b.call(accessors, value)) !== null && _c !== void 0 ? _c : 0;
                return yValue + y1Value;
            });
            return Math.max.apply(Math, activeSeries);
        }));
        return [0, yDomainMax];
    }, [series, accessors, activeSet]);
    var yScale = React.useMemo(function () {
        return scaleLinear({
            domain: yDomain
        })
            .range([boundedHeight, 0])
            .nice();
    }, [series, accessors, boundedHeight, yDomain]);
    var barGroupScale = React.useMemo(function () {
        return scaleBand({
            domain: xDomain,
            padding: 0.3
        }).range([0, boundedWidth]);
    }, [series, accessors, boundedWidth]);
    var xAxisScale = React.useMemo(function () {
        return scaleBand({
            domain: xAxisDomain,
            padding: 0.3
        }).range([0, boundedWidth]);
    }, [xAxisDomain, boundedWidth]);
    var colorScale = React.useMemo(function () {
        return scaleOrdinal({
            domain: groupKeys,
            range: range(groupKeys.length).map(scaleLinear({
                domain: [0, groupKeys.length - 1],
                range: colorPalette
            }).interpolate(interpolateLab))
        });
    }, [colorPalette, groupKeys]);
    var axesConfig = React.useMemo(function () {
        return getAxesConfig(axesProps, yScale, xAxisScale, boundedHeight, margins);
    }, [axesProps, yScale, barGroupScale, boundedHeight, margins]);
    var handleLegendClick = function (activeSet) {
        setActiveSet(activeSet);
    };
    // create a transformation for legends based on groupedKeys
    var legendSeries = React.useMemo(function () {
        return groupKeys.reduce(function (acc, key) {
            acc[key] = {
                key: key,
                name: key
            };
            return acc;
        }, {});
    }, [groupKeys]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Canvas, { height: height, margins: margins },
            !hideGrid && (React.createElement(GridRows, { scale: yScale, numTicks: axesConfig.left.numTicks })),
            React.createElement(BarGroups, { series: series, accessors: accessors, yScale: yScale, barGroupScale: barGroupScale, colorScale: colorScale, crosshatchColor: crosshatchColor, tooltip: tooltip, activeSet: activeSet }),
            React.createElement(Axis, __assign({}, axesConfig.left)),
            React.createElement(Axis, __assign({}, axesConfig.bottom))),
        !hideLegend && (React.createElement(Legend, { series: legendSeries, accessors: {
                key: function (d) { return d.key; },
                name: function (d) { return d.name; }
            }, activeSet: activeSet, onToggle: handleLegendClick, colorScheme: colorScale, legendFormat: legendFormat, marginLeft: margins.left, marginRight: margins.right }))));
}
//# sourceMappingURL=GroupedBar.js.map